import { DeleteOutlined, PictureOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import { Button, Form, Input, InputNumber, Modal, message, Select, Switch, Upload } from "antd";
import { useEffect, useMemo, useState } from "react";
import type { Organization } from "@/modules/organizations/types/organization.types";
import type { BuildingCreatePayload } from "../api/buildings.api";
import type { Building } from "../types/objects.types";

const { TextArea } = Input;
const { Dragger } = Upload;

type BuildingCreateFormValues = {
	address?: string;
	code?: string;
	district?: string;
	isActive?: boolean;
	name: string;
	number?: number;
	orgId?: string;
	showroomUrl?: string;
};

interface BuildingCreateModalProps {
	initialBuilding?: Building;
	isSaving?: boolean;
	organizations?: Organization[];
	requireOrganization?: boolean;
	visible: boolean;
	onClose: () => void;
	onSave: (
		building: BuildingCreatePayload & { isActive?: boolean },
		imageFile?: File
	) => Promise<void>;
}

export function BuildingCreateModal({
	initialBuilding,
	isSaving = false,
	organizations = [],
	requireOrganization = false,
	visible,
	onClose,
	onSave,
}: BuildingCreateModalProps) {
	const [form] = Form.useForm<BuildingCreateFormValues>();
	const [imageFile, setImageFile] = useState<File>();
	const [fileList, setFileList] = useState<UploadFile[]>([]);

	const previewUrl = useMemo(() => {
		if (!imageFile) {
			return undefined;
		}
		return URL.createObjectURL(imageFile);
	}, [imageFile]);
	const isEditMode = Boolean(initialBuilding);

	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	const resetModal = () => {
		form.resetFields();
		setImageFile(undefined);
		setFileList([]);
	};

	useEffect(() => {
		if (!visible) {
			return;
		}

		if (initialBuilding) {
			form.setFieldsValue({
				address: initialBuilding.address,
				code: initialBuilding.code || undefined,
				district: initialBuilding.district,
				isActive: initialBuilding.isActive !== false,
				name: initialBuilding.name,
				number: initialBuilding.number,
				showroomUrl: initialBuilding.showroomUrl,
			});
			setImageFile(undefined);
			setFileList([]);
			return;
		}

		form.setFieldsValue({ isActive: true, number: 1 });
	}, [form, initialBuilding, visible]);

	const handleClose = () => {
		if (isSaving) {
			return;
		}
		resetModal();
		onClose();
	};

	const beforeUpload: UploadProps["beforeUpload"] = (file) => {
		const isValidType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
		if (!isValidType) {
			message.error("Faqat JPG, PNG yoki WEBP rasm yuklang.");
			return Upload.LIST_IGNORE;
		}

		const isValidSize = file.size <= 10 * 1024 * 1024;
		if (!isValidSize) {
			message.error("Rasm hajmi 10MB dan oshmasligi kerak.");
			return Upload.LIST_IGNORE;
		}

		setImageFile(file);
		setFileList([file]);
		return false;
	};

	const handleOk = () => {
		form
			.validateFields()
			.then(async (values) => {
				await onSave(
					{
						name: values.name,
						code: values.code || undefined,
						orgId: values.orgId || undefined,
						number: values.number && values.number > 0 ? values.number : undefined,
						address: values.address || undefined,
						district: values.district || undefined,
						showroomUrl: values.showroomUrl || undefined,
						...(isEditMode ? { isActive: values.isActive !== false } : {}),
					},
					imageFile
				);
				resetModal();
				onClose();
			})
			.catch(() => undefined);
	};

	return (
		<Modal
			width={520}
			cancelButtonProps={{ disabled: isSaving }}
			cancelText="Bekor"
			centered
			className="crm-scrollable-modal crm-object-modal crm-building-modal"
			maskClosable={!isSaving}
			okButtonProps={{ className: "crm-primary-btn", loading: isSaving }}
			okText="Saqlash"
			onCancel={handleClose}
			onOk={handleOk}
			open={visible}
			title={isEditMode ? "Binoni tahrirlash" : "Yangi bino"}
		>
			<Form
				form={form}
				initialValues={{ isActive: true, number: 1 }}
				layout="vertical"
				requiredMark={false}
				style={{ marginTop: 0 }}
			>
				{requireOrganization ? (
					<div className="crm-inline-fields">
						<Form.Item
							label="Tashkilot"
							name="orgId"
							rules={[{ required: true, message: "Tashkilotni tanlang!" }]}
						>
							<Select
								allowClear
								options={organizations.map((organization) => ({
									label: `${organization.name} (${organization.code})`,
									value: organization.id,
								}))}
								placeholder="Tashkilotni tanlang"
								showSearch
								optionFilterProp="label"
							/>
						</Form.Item>
						<Form.Item
							label="Nomi"
							name="name"
							rules={[{ required: true, message: "Bino nomini kiriting!" }]}
						>
							<Input placeholder="Masalan: Dream" />
						</Form.Item>
					</div>
				) : (
					<Form.Item
						label="Nomi"
						name="name"
						rules={[{ required: true, message: "Bino nomini kiriting!" }]}
					>
						<Input placeholder="Masalan: Dream" />
					</Form.Item>
				)}

				<div className="crm-inline-fields crm-inline-mobile">
					<Form.Item label="Kod" name="code">
						<Input placeholder="samo" />
					</Form.Item>

					<Form.Item label="Tartib raqami" name="number">
						<InputNumber min={1} placeholder="1" style={{ width: "100%" }} />
					</Form.Item>
				</div>

				<Form.Item label="Tuman" name="district">
					<Input placeholder="Masalan: Yunusobod tumani" />
				</Form.Item>

				<Form.Item label="Manzil" name="address">
					<TextArea placeholder="Viloyat, shahar, tuman, ko'cha" rows={2} />
				</Form.Item>

				{isEditMode ? (
					<div className="crm-inline-fields crm-inline-mobile">
						<Form.Item label="Showroom 3D havola" name="showroomUrl" style={{ flex: 3 }}>
							<Input placeholder="https://..." />
						</Form.Item>
						<Form.Item label="Holati" name="isActive" valuePropName="checked" style={{ flex: 1 }}>
							<Switch checkedChildren="Faol" unCheckedChildren="Nofaol" />
						</Form.Item>
					</div>
				) : (
					<Form.Item label="Showroom 3D havola" name="showroomUrl">
						<Input placeholder="https://..." />
					</Form.Item>
				)}

				<div className="crm-field crm-building-upload-field">
					<span>Fasad rasmi</span>
					<Dragger
						accept="image/png,image/jpeg,image/webp"
						beforeUpload={beforeUpload}
						fileList={fileList}
						maxCount={1}
						onRemove={() => {
							setImageFile(undefined);
							setFileList([]);
							return true;
						}}
						showUploadList={false}
					>
						{previewUrl ? (
							<div className="crm-building-upload-preview">
								<img alt="Fasad rasmi" src={previewUrl} />
								<Button
									danger
									icon={<DeleteOutlined />}
									onClick={(event) => {
										event.stopPropagation();
										setImageFile(undefined);
										setFileList([]);
									}}
									size="small"
									type="text"
								>
									O'chirish
								</Button>
							</div>
						) : (
							<div className="crm-building-upload-empty">
								<PictureOutlined style={{ fontSize: "18px" }} />
								<span>JPG/PNG/WEBP - max 10MB</span>
							</div>
						)}
					</Dragger>
				</div>
			</Form>
		</Modal>
	);
}
